// A small advertising board for xenscope on the dashboard: one full-width card,
// ‹ › at each end, dots below. It moves on by itself (every INTERVAL_MS) until
// the installer uses the arrows or dots, and pauses while hovered or focused.
// Links go to xenscope.com with UTM tags so the referral shows up in analytics
// (and the browser's own Referer: the links don't use rel=noreferrer).
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, BatteryCharging, ChevronLeft, ChevronRight, Flame, Fan, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";

const INTERVAL_MS = 6000;
const XENSCOPE = "https://xenscope.com";

interface Slide {
  key: string; // also the utm_campaign
  icon: LucideIcon;
  title: string;
  body: string;
  cta: string;
}

const SLIDES: Slide[] = [
  {
    key: "ashp-leads",
    icon: Fan,
    title: "Find more leads for heat pump installs",
    body: "See the homes in your area most ready for an air source heat pump, from EPC and property data, before anyone else knocks on the door.",
    cta: "Find heat pump leads",
  },
  {
    key: "ashp-servicing",
    icon: Wrench,
    title: "Find heat pumps that need servicing",
    body: "Homes near you that already have an air source heat pump: a steady list of service and maintenance work, street by street.",
    cta: "Find servicing work",
  },
  {
    key: "pv-battery",
    icon: BatteryCharging,
    title: "Find solar PV homes ready for a battery",
    body: "Properties with solar panels and no battery yet: the natural next install, and a warm conversation to start.",
    cta: "Find battery prospects",
  },
  {
    key: "off-gas",
    icon: Flame,
    title: "Find off-gas homes on oil and LPG",
    body: "Homes off the gas grid heated by oil or LPG can get the £9,000 Boiler Upgrade Scheme grant. Find them before the uplift ends.",
    cta: "Find off-gas homes",
  },
];

/** xenscope link with referral tags for this slide. */
const href = (s: Slide) => `${XENSCOPE}/?utm_source=xenquote&utm_medium=console&utm_campaign=${s.key}&ref=xenquote`;

export function PromoCarousel() {
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(true); // off once the installer takes control
  const [paused, setPaused] = useState(false); // hovered or focused
  const reduced = useRef(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

  const go = useCallback((i: number) => setIndex((i + SLIDES.length) % SLIDES.length), []);

  // Move on by itself until someone clicks, and never under reduced motion
  useEffect(() => {
    if (!auto || paused || reduced.current) return;
    const t = window.setTimeout(() => go(index + 1), INTERVAL_MS);
    return () => window.clearTimeout(t);
  }, [auto, paused, index, go]);

  const user = (i: number) => {
    setAuto(false);
    go(i);
  };

  return (
    <section
      className="card promo"
      aria-roledescription="carousel"
      aria-label="xenscope"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <button type="button" className="promo-arrow promo-prev" onClick={() => user(index - 1)} aria-label="Previous">
        <ChevronLeft size={22} aria-hidden="true" />
      </button>

      <div className="promo-window">
        {/* All slides in a row; the track slides to the current one */}
        <div className="promo-track" style={{ transform: `translateX(-${index * 100}%)` }}>
          {SLIDES.map((s, i) => (
            <article key={s.key} className="promo-slide" aria-roledescription="slide" aria-label={`${i + 1} of ${SLIDES.length}`} aria-hidden={i !== index}>
              <span className="promo-icon">
                <s.icon size={26} aria-hidden="true" />
              </span>
              <div className="promo-copy">
                <span className="promo-kicker">xenscope</span>
                <h2>{s.title}</h2>
                <p>{s.body}</p>
              </div>
              <a className="btn btn-accent promo-cta" href={href(s)} target="_blank" rel="noopener" tabIndex={i === index ? 0 : -1}>
                {s.cta} <ArrowRight size={16} aria-hidden="true" />
              </a>
            </article>
          ))}
        </div>
      </div>

      <button type="button" className="promo-arrow promo-next" onClick={() => user(index + 1)} aria-label="Next">
        <ChevronRight size={22} aria-hidden="true" />
      </button>

      <div className="promo-dots" role="tablist" aria-label="Choose a message">
        {SLIDES.map((s, i) => (
          <button key={s.key} type="button" role="tab" aria-selected={i === index} aria-label={s.title} className={i === index ? "is-on" : ""} onClick={() => user(i)}>
            {/* The current dot fills over the interval while the board moves by itself */}
            {i === index && auto && !paused && !reduced.current && <span className="promo-timer" style={{ animationDuration: `${INTERVAL_MS}ms` }} />}
          </button>
        ))}
      </div>
    </section>
  );
}
